import { LightIcon, PlusIcon } from "@xola/icons";
import { Alert, Button } from "@xola/ui-kit";
import React, { useEffect, useState } from "react";
import { Affiliate, Permission, PurchaseRuleLink } from "types";
import { useCollection } from "../../hooks/useCollection";
import { IsGranted } from "../Common/IsGranted";
import { SecondaryButton } from "../Common/SecondaryButton";
import { ContentLayout } from "../Layout/ContentLayout";
import { Loader } from "../Loader";
import { ApplyTermsTemplateModal, useUpdateTermsTemplate } from "./TermsTemplate/ApplyTermsTemplateModal";
import { EditTermsTemplateForm } from "./TermsTemplate/EditTermsTemplateForm";

interface CommercialTermsProps {
    agent: Affiliate;
}

export const CommercialTerms = ({ agent }: CommercialTermsProps) => {
    const { data: appliedTermsTemplates, isLoading } = useCollection<PurchaseRuleLink>("purchaseRuleLinks", {
        "tags._id": "Affiliate",
        "entities.id": agent.id,
    });
    const appliedTermsTemplate = appliedTermsTemplates[0] ?? undefined;

    const { data: termsTemplates, isLoading: isTermTemplateLoading } = useCollection<PurchaseRuleLink>(
        "purchaseRuleLinks",
        {
            "tags._id": "Affiliate",
            limit: 1,
        },
    );
    const hasTermTemplate = termsTemplates.length > 0;

    if (isLoading || isTermTemplateLoading) {
        return <Loader />;
    }

    return (
        <IsGranted permissions={Permission.PARTNERS_COMMERCIAL_TERMS_VIEW}>
            <CommercialTermForm
                agent={agent}
                appliedTermsTemplate={appliedTermsTemplate}
                hasTermTemplate={hasTermTemplate}
            />
        </IsGranted>
    );
};

interface CommercialTermFormProps {
    agent: Affiliate;
    hasTermTemplate: boolean;
    appliedTermsTemplate?: PurchaseRuleLink;
}

export const CommercialTermForm = ({ agent, appliedTermsTemplate, hasTermTemplate }: CommercialTermFormProps) => {
    const [isApplyTermsTemplateModalOpen, setIsApplyTermsTemplateModalOpen] = useState(false);
    const [termsTemplate, setTermsTemplate] = useState<PurchaseRuleLink | undefined>(appliedTermsTemplate);
    const [showEditTermsTemplateForm, setShowEditTermsTemplateForm] = useState(false);
    const selectedAgent = [agent.id];

    const handleNewTemplateClick = () => {
        setTermsTemplate(undefined);
        setShowEditTermsTemplateForm(true);
    };

    const handleApplyTemplateClick = () => {
        setIsApplyTermsTemplateModalOpen(true);
    };

    const { mutateAsync: updateTermsTemplate } = useUpdateTermsTemplate();

    const handleTemplateSave = (purchaseRuleLink: PurchaseRuleLink) => {
        updateTermsTemplate({ agentIds: selectedAgent, selectedTemplate: purchaseRuleLink });
    };

    useEffect(() => {
        if (appliedTermsTemplate) {
            setTermsTemplate(appliedTermsTemplate);
        }
    }, [appliedTermsTemplate]);

    return (
        <>
            <ContentLayout classNames={{ root: "!mb-0 pb-0" }}>
                <Alert>
                    <div className="flex">
                        <LightIcon size="medium" className="mt-1 w-12" />
                        <CommercialTermAlertSummary
                            hasTermTemplate={hasTermTemplate}
                            appliedTermsTemplate={appliedTermsTemplate}
                            handleNewTemplateClick={handleNewTemplateClick}
                            handleApplyTemplateClick={handleApplyTemplateClick}
                        />
                    </div>
                </Alert>

                <ApplyTermsTemplateModal
                    isOpen={isApplyTermsTemplateModalOpen}
                    agents={selectedAgent}
                    selectedTermsTemplate={appliedTermsTemplate}
                    onClose={() => setIsApplyTermsTemplateModalOpen(false)}
                />
            </ContentLayout>

            {(showEditTermsTemplateForm || termsTemplate) && (
                <EditTermsTemplateForm
                    showAgentFormOptions
                    termsTemplate={termsTemplate}
                    onSuccess={handleTemplateSave}
                />
            )}
        </>
    );
};

interface CommercialTermAlertSummaryProps {
    hasTermTemplate: boolean;
    appliedTermsTemplate?: PurchaseRuleLink;
    handleNewTemplateClick: () => void;
    handleApplyTemplateClick: () => void;
}

export const CommercialTermAlertSummary = ({
    hasTermTemplate,
    appliedTermsTemplate,
    handleNewTemplateClick,
    handleApplyTemplateClick,
}: CommercialTermAlertSummaryProps) => {
    if (!hasTermTemplate) {
        return (
            <div className="w-full space-y-3 pr-4">
                <h4 className="font-bold">Create a New Terms Template</h4>
                <p>
                    You currently do not have any Terms Templates defined. Let’s start by creating a new Terms Template
                    and associating it with this Agent.
                </p>
                <Button size="small" color="primary" icon={<PlusIcon />} onClick={handleNewTemplateClick}>
                    Create New Template
                </Button>
            </div>
        );
    }

    if (appliedTermsTemplate) {
        return (
            <div className="w-full space-y-3 pr-4">
                <h4 className="font-bold">{appliedTermsTemplate.name} Template Applied</h4>
                <div className="flex justify-end space-x-2">
                    <SecondaryButton size="small" onClick={handleApplyTemplateClick}>
                        Apply Different Template
                    </SecondaryButton>
                    <SecondaryButton size="small" onClick={handleNewTemplateClick}>
                        Create New Template
                    </SecondaryButton>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full space-y-3 pr-4">
            <h4 className="font-bold">Apply or Create a New Terms Template</h4>
            <div className="flex justify-end space-x-2">
                <SecondaryButton size="small" icon={<PlusIcon />} onClick={handleNewTemplateClick}>
                    Create New Template
                </SecondaryButton>
                <Button size="small" color="primary" onClick={handleApplyTemplateClick}>
                    Apply Template
                </Button>
            </div>
        </div>
    );
};
